import './Home.css';
import LoadingScreen from './LoadingScreen';
import { useState, useEffect, useRef } from 'react';

function Home() {
  const [isLoading, setIsLoading] = useState(true);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [cursorExpanded, setCursorExpanded] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const heroRef = useRef(null);

  // ── Loading logic (unchanged from original) ──
  useEffect(() => {
    const minTimer = setTimeout(() => {
      const checkSplineLoaded = () => {
        const splineViewer = document.querySelector('spline-viewer');
        if (splineViewer) {
          const shadowRoot = splineViewer.shadowRoot;
          if (shadowRoot) {
            const canvas = shadowRoot.querySelector('canvas');
            if (canvas) {
              setTimeout(() => setIsLoading(false), 300);
              return true;
            }
          }
        }
        return false;
      };
      if (!checkSplineLoaded()) {
        const checkInterval = setInterval(() => {
          if (checkSplineLoaded()) clearInterval(checkInterval);
        }, 200);
        setTimeout(() => {
          clearInterval(checkInterval);
          setIsLoading(false);
        }, 3000);
      }
    }, 5000);
    return () => clearTimeout(minTimer);
  }, []);

  // ── Trigger hero entrance after loading ──
  useEffect(() => {
    if (!isLoading) {
      const t = setTimeout(() => setHeroVisible(true), 100);
      return () => clearTimeout(t);
    }
  }, [isLoading]);

  // ── Custom cursor tracking ──
  useEffect(() => {
    const move = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', move);
    return () => window.removeEventListener('mousemove', move);
  }, []);

  // ── Parallax on image: subtle mouse-reactive tilt ──
  const parallaxStyle = {
    transform: `
      translate(
        ${(mousePos.x / window.innerWidth - 0.5) * -12}px,
        ${(mousePos.y / window.innerHeight - 0.5) * -8}px
      )
    `,
    transition: 'transform 0.15s ease-out',
  };

  const interactiveEls = { onMouseEnter: () => setCursorExpanded(true), onMouseLeave: () => setCursorExpanded(false) };

  return (
    <div className="home-root">
      {/* Custom cursor */}
      <div
        className={`home-cursor ${cursorExpanded ? 'home-cursor--expanded' : ''}`}
        style={{ left: mousePos.x, top: mousePos.y }}
      />

      <LoadingScreen className={isLoading ? '' : 'hidden'} />

      <div className={`home-fade-in ${!isLoading ? 'home-fade-in--visible' : ''}`}>

        {/* ── NAV ── */}
        {/* <nav className="home-nav">
          <span className="home-nav__logo">YELANI.DESIGN</span>
          <ul className="home-nav__links">
            {['about', 'work', 'linkedin'].map((item) => (
              <li key={item}>
                <a href={`/${item}`} className="home-nav__link" {...interactiveEls}>
                  {item}
                </a>
              </li>
            ))}
          </ul>
        </nav> */}

        {/* ── HERO ── */}
        <main className="home-hero" ref={heroRef}>

          {/* Left column: role + bio */}
          <div className={`home-hero__left ${heroVisible ? 'home-hero__left--visible' : ''}`}>
            <p className="home-hero__eyebrow">UI / UX Engineer</p>
            <p className="home-hero__bio">
              Crafting interfaces where clarity and craft work together — so your users always know exactly what to do next.
            </p>
            <a href="/work" className="home-hero__cta" {...interactiveEls}>
              View Work <span className="home-hero__cta-arrow">↗</span>
            </a>
          </div>

          {/* Centre: portrait + giant name */}
          <div className={`home-hero__centre ${heroVisible ? 'home-hero__centre--visible' : ''}`}>
            <div className="home-hero__portrait-wrap" style={parallaxStyle}>
              {/* Decorative ring */}
              <div className="home-hero__ring" />
              {/* Photo */}
              <img
                src="/pic3.png"
                alt="Yelani"
                className="home-hero__portrait"
              />
              {/* Floating badge */}
              <div className="home-hero__badge">
                <span className="home-hero__badge-num">20+</span>
                <span className="home-hero__badge-label">Projects</span>
              </div>
            </div>
            {/* Name sits behind the image via z-index */}
            <span className="home-hero__name-bg" aria-hidden="true">Yelani</span>
          </div>

          {/* Right column: stats + year */}
          <div className={`home-hero__right ${heroVisible ? 'home-hero__right--visible' : ''}`}>
            <div className="home-hero__stat">
              <span className="home-hero__stat-num">2+</span>
              <span className="home-hero__stat-label">Years<br />Experience</span>
            </div>
            <div className="home-hero__divider" />
            <div className="home-hero__stat">
              <span className="home-hero__stat-num">98%</span>
              <span className="home-hero__stat-label">Client<br />Satisfaction</span>
            </div>
            <p className="home-hero__year">© 2025</p>
          </div>

          {/* Scroll hint */}
          <div className="home-hero__scroll">
            <span className="home-hero__scroll-line" />
            <span className="home-hero__scroll-label">Scroll</span>
          </div>
        </main>

        {/* ── SKILLS MARQUEE ── */}
        <div className="home-marquee">
          <div className="home-marquee__track">
            {[
              'User Experience', 'Interface Design', 'Design Systems',
              'Prototyping', 'User Research',
              'Motion Design', 'Accessibility',
              'User Experience', 'Interface Design', 'Design Systems',
              'Prototyping', 'User Research',
              'Motion Design', 'Accessibility',
            ].map((skill, i) => (
              <span key={i} className="home-marquee__item">
                {skill}
                <span className="home-marquee__dot" aria-hidden="true" />
              </span>
            ))}
          </div>
        </div>

        {/* ── SELECTED WORK ── */}
        <section className="home-work">
          <p className="home-work__label">Selected Work</p>
          <div className="home-work__grid">
            {/* Old projects commented out:
              {
                num: '01',
                tag: 'Product Design · 2024',
                title: 'Fintech Dashboard\nRedesign',
                desc: 'End-to-end redesign of a B2B platform serving 200k+ users. Reduced task completion time by 38%.',
                featured: true,
              },
              {
                num: '02',
                tag: 'Design System · 2023',
                title: 'Atomic Design\nSystem',
                desc: 'Scalable component library across 6 product lines. Eliminated 60% of design debt.',
              },
              {
                num: '03',
                tag: 'Mobile App · 2023',
                title: 'Health & Wellness\niOS App',
                desc: 'Onboarding redesign lifted D7 retention by 22% and App Store rating from 3.8 → 4.7.',
              },
            */}
            {[
              {
                num: '01',
                tag: 'Case Study · UX Design',
                title: 'HerBiz',
                desc: 'A platform designed to empower women entrepreneurs with tools and resources to grow their businesses.',
                featured: true,
                link: 'https://www.figma.com/design/KUo22BRe72JTRYMzKRd8Z6/HerBiz-Case-Study?node-id=2651-4613&t=cCQLajqVSP9APsiV-1',
              },
              {
                num: '02',
                tag: 'Case Study · Accessibility',
                title: 'Eye Pal',
                desc: 'An accessibility-focused app designed to assist visually impaired users in navigating daily tasks with ease.',
                featured: true,
                link: 'https://www.figma.com/design/uUKCi4wOWhNzYAp0U5eLqj/Eye-Pal?node-id=405-10418&t=rWlA4VYRdbC33q7s-1',
              },
            ].map((project, i) => (
              <a
                key={i}
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className={`home-work__card ${project.featured ? 'home-work__card--featured' : ''}`}
                style={{ animationDelay: `${i * 0.12}s`, textDecoration: 'none' }}
                {...interactiveEls}
              >
                <span className="home-work__card-num" aria-hidden="true">{project.num}</span>
                <span className="home-work__card-tag">{project.tag}</span>
                <h2 className="home-work__card-title">{project.title}</h2>
                <p className="home-work__card-desc">{project.desc}</p>
                <span className="home-work__card-arrow">↗</span>
              </a>
            ))}
          </div>
        </section>

      </div>
    </div>
  );
}

export default Home;